package org.catharsis.spendtel.feature.presentation.web

import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.spring
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ChevronLeft
import androidx.compose.material.icons.filled.ChevronRight
import androidx.compose.material.icons.filled.ExpandLess
import androidx.compose.material.icons.filled.ExpandMore
import androidx.compose.material.icons.outlined.AutoAwesome
import androidx.compose.material.icons.outlined.Psychology
import androidx.compose.material.icons.outlined.Repeat
import androidx.compose.material.icons.outlined.ShoppingBag
import androidx.compose.material.icons.outlined.Timeline
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.catharsis.spendtel.feature.data.ExpenseReport
import org.catharsis.spendtel.feature.data.MerchantInsight
import org.catharsis.spendtel.feature.data.MoneyLeaks
import org.catharsis.spendtel.feature.data.ReportTrends
import org.catharsis.spendtel.feature.data.SpendingEquivalence
import org.catharsis.spendtel.feature.data.SpendingPatterns
import org.catharsis.spendtel.feature.data.WhatChanged
import org.catharsis.spendtel.feature.data.formatCurrency
import org.catharsis.spendtel.feature.data.formatPeriodRange
import org.catharsis.spendtel.feature.share.ShareImageCard
import org.catharsis.spendtel.feature.share.highlightShareContent
import org.catharsis.spendtel.feature.share.shareContent
import org.catharsis.spendtel.feature.presentation.expensereport.AboutYouSection
import org.catharsis.spendtel.feature.presentation.expensereport.ExpenseReportStoryContent
import org.catharsis.spendtel.feature.presentation.expensereport.GoalInsightsSection
import org.catharsis.spendtel.feature.presentation.expensereport.GradientHeroCard
import org.catharsis.spendtel.feature.presentation.expensereport.LifeCategoriesSection
import org.catharsis.spendtel.feature.presentation.expensereport.ReportSectionHeader
import org.catharsis.spendtel.feature.presentation.expensereport.ShareCardActions
import org.catharsis.spendtel.feature.presentation.expensereport.SoftInsightCard
import org.catharsis.spendtel.feature.presentation.expensereport.SpendTelGradients
import org.catharsis.spendtel.feature.presentation.expensereport.SpendTelReportColors
import org.catharsis.spendtel.feature.presentation.expensereport.SpendingStoriesSection
import org.catharsis.spendtel.feature.presentation.expensereport.SpendingTimelineSection
import org.catharsis.spendtel.feature.presentation.expensereport.StoryModulePanel
import org.catharsis.spendtel.feature.presentation.expensereport.WhatChangedSection
import org.catharsis.spendtel.feature.presentation.expensereport.accentForIndex
import org.catharsis.spendtel.feature.presentation.expensereport.ExpenseReportBody
import org.catharsis.spendtel.feature.presentation.expensereport.ExpenseReportStatementRow

private val wideLayoutBreakpoint = 900.dp

@Composable
fun WebExpenseReportBody(
    report: ExpenseReport,
    modifier: Modifier = Modifier,
) {
    when {
        report.isProcessing || report.isFailed -> {
            ExpenseReportBody(
                report = report,
                modifier = modifier,
                separatedModules = true,
            )
        }
        else -> {
            Column(
                modifier = modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(32.dp),
            ) {
                WebExpenseReportStoryContent(report = report)
                if (report.sourceDocuments.isNotEmpty()) {
                    WebReportSourceStatements(report = report)
                }
            }
        }
    }
}

@Composable
private fun WebReportSourceStatements(report: ExpenseReport) {
    var expanded by remember { mutableStateOf(false) }
    StoryModulePanel {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clickable { expanded = !expanded },
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    text = "Source statements (${report.sourceDocuments.size})",
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.SemiBold,
                    color = SpendTelReportColors.ink,
                )
                Icon(
                    imageVector = if (expanded) Icons.Filled.ExpandLess else Icons.Filled.ExpandMore,
                    contentDescription = null,
                    tint = SpendTelReportColors.mint,
                )
            }
            if (expanded) {
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    report.sourceDocuments.forEach { document ->
                        ExpenseReportStatementRow(document = document)
                    }
                }
            }
        }
    }
}

@Composable
fun WebExpenseReportStoryContent(
    report: ExpenseReport,
    modifier: Modifier = Modifier,
) {
    BoxWithConstraints(modifier = modifier.fillMaxWidth()) {
        if (maxWidth < wideLayoutBreakpoint) {
            ExpenseReportStoryContent(
                report = report,
                separatedModules = true,
            )
            return@BoxWithConstraints
        }

        Column(verticalArrangement = Arrangement.spacedBy(28.dp)) {
            WebStoryHeroRow(report = report)

            if (report.highlights.isNotEmpty()) {
                WebHighlightCarousel(highlights = report.highlights)
            }

            if (report.equivalences.isNotEmpty()) {
                WebEquivalenceExplorer(
                    equivalences = report.equivalences,
                    currency = report.dashboardCurrency(),
                )
            }

            WebQuickStatsStrip(report = report)

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(24.dp),
            ) {
                Column(
                    modifier = Modifier.weight(1f),
                    verticalArrangement = Arrangement.spacedBy(24.dp),
                ) {
                    report.trends?.let { trends ->
                        WebTrendsPanel(trends = trends)
                    } ?: run {
                        if (!report.hasComparisonContent && report.isComplete) {
                            WebFirstReportComparisonPanel()
                        }
                    }
                    report.whatChanged?.let { WebWhatChangedCompact(changes = it) }
                    report.spendingPatterns?.let { patterns ->
                        WebRhythmsPanel(
                            patterns = patterns,
                            currency = report.dashboardCurrency(),
                        )
                    }
                }
                Column(
                    modifier = Modifier.weight(1f),
                    verticalArrangement = Arrangement.spacedBy(24.dp),
                ) {
                    if (report.goalInsights.isNotEmpty()) {
                        StoryModulePanel {
                            GoalInsightsSection(insights = report.goalInsights)
                        }
                    }
                    report.moneyLeaks?.let { leaks ->
                        WebMoneyLeaksPanel(
                            moneyLeaks = leaks,
                            currency = report.dashboardCurrency(),
                        )
                    }
                }
            }

            if (report.merchantInsights.isNotEmpty()) {
                WebMerchantGrid(
                    merchants = report.merchantInsights,
                    currency = report.dashboardCurrency(),
                )
            }

            if (report.spendingStories.isNotEmpty()) {
                WebCollapsibleStoryModule(
                    title = "Spending stories",
                    subtitle = "Themed chapters from your month",
                    initiallyExpanded = true,
                ) {
                    SpendingStoriesSection(stories = report.spendingStories)
                }
            }

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(24.dp),
            ) {
                if (report.spendingTimeline.isNotEmpty()) {
                    Box(modifier = Modifier.weight(1f)) {
                        WebCollapsibleStoryModule(
                            title = "Spending timeline",
                            subtitle = "Your month as a narrative arc",
                        ) {
                            SpendingTimelineSection(entries = report.spendingTimeline)
                        }
                    }
                }
                if (report.lifeCategories.isNotEmpty()) {
                    Box(modifier = Modifier.weight(1f)) {
                        WebCollapsibleStoryModule(
                            title = "Life categories",
                            subtitle = "Where your money meets your life",
                        ) {
                            LifeCategoriesSection(
                                categories = report.lifeCategories,
                                currency = report.dashboardCurrency(),
                            )
                        }
                    }
                }
            }

            if (report.aboutYou.isNotEmpty()) {
                WebCollapsibleStoryModule(
                    title = "What your spending says about you",
                    subtitle = "Reflections worth sitting with",
                ) {
                    AboutYouSection(lines = report.aboutYou)
                }
            }
        }
    }
}

@Composable
private fun WebStoryHeroRow(report: ExpenseReport) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(24.dp),
    ) {
        Box(modifier = Modifier.weight(1.35f)) {
            report.monthlyStory?.let { story ->
                GradientHeroCard(gradient = SpendTelGradients.hero) {
                    Text(
                        text = "YOUR MONTHLY STORY",
                        style = MaterialTheme.typography.labelMedium,
                        color = Color.White.copy(alpha = 0.72f),
                        letterSpacing = 1.2.sp,
                    )
                    Text(
                        text = story.headline,
                        style = MaterialTheme.typography.headlineLarge,
                        fontWeight = FontWeight.Bold,
                        color = Color.White,
                        lineHeight = 38.sp,
                    )
                    report.summary?.let { summary ->
                        formatPeriodRange(summary.periodStart, summary.periodEnd)?.let { period ->
                            Text(
                                text = period,
                                style = MaterialTheme.typography.bodyLarge,
                                color = Color.White.copy(alpha = 0.85f),
                            )
                        }
                        Text(
                            text = formatCurrency(summary.totalSpending, summary.currency),
                            style = MaterialTheme.typography.displaySmall,
                            fontWeight = FontWeight.Bold,
                            color = Color.White,
                        )
                    }
                    story.observations.firstOrNull()?.let { observation ->
                        Text(
                            text = observation,
                            style = MaterialTheme.typography.bodyLarge,
                            color = Color.White.copy(alpha = 0.92f),
                            lineHeight = 26.sp,
                        )
                    }
                }
            } ?: report.summary?.let { summary ->
                GradientHeroCard(gradient = SpendTelGradients.storyWarm) {
                    formatPeriodRange(summary.periodStart, summary.periodEnd)?.let { period ->
                        Text(text = period, color = Color.White.copy(alpha = 0.9f))
                    }
                    Text(
                        text = formatCurrency(summary.totalSpending, summary.currency),
                        style = MaterialTheme.typography.displaySmall,
                        fontWeight = FontWeight.Bold,
                        color = Color.White,
                    )
                }
            }
        }

        Column(
            modifier = Modifier.weight(1f),
            verticalArrangement = Arrangement.spacedBy(16.dp),
        ) {
            report.financialPersonality?.let { personality ->
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clip(RoundedCornerShape(24.dp))
                        .background(SpendTelGradients.personality)
                        .padding(22.dp),
                ) {
                    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                        Row(
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Icon(
                                imageVector = Icons.Outlined.Psychology,
                                contentDescription = null,
                                tint = Color.White.copy(alpha = 0.9f),
                            )
                            Text(
                                text = "YOUR PERSONALITY",
                                style = MaterialTheme.typography.labelMedium,
                                color = Color.White.copy(alpha = 0.75f),
                                letterSpacing = 1.sp,
                            )
                        }
                        Text(
                            text = personality.label,
                            style = MaterialTheme.typography.headlineSmall,
                            fontWeight = FontWeight.Bold,
                            color = Color.White,
                        )
                        Text(
                            text = personality.description,
                            style = MaterialTheme.typography.bodyMedium,
                            color = Color.White.copy(alpha = 0.9f),
                            lineHeight = 22.sp,
                        )
                    }
                }
            }

            report.highlights.firstOrNull()?.let { teaser ->
                StoryModulePanel {
                    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        Text(
                            text = "Spotlight",
                            style = MaterialTheme.typography.labelLarge,
                            color = SpendTelReportColors.inkMuted,
                        )
                        Text(
                            text = "\"$teaser\"",
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.SemiBold,
                            color = SpendTelReportColors.ink,
                            lineHeight = 28.sp,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun WebHighlightCarousel(highlights: List<String>) {
    var index by remember(highlights) { mutableIntStateOf(0) }
    val safeIndex = index.coerceIn(0, highlights.lastIndex)

    StoryModulePanel {
        Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
            ReportSectionHeader(
                title = "Story highlights",
                subtitle = "Tap through share-worthy one-liners",
                accent = SpendTelReportColors.amber,
                icon = Icons.Outlined.AutoAwesome,
            )
            ShareCardActions(
                content = highlightShareContent(highlights[safeIndex]),
                imageCard = ShareImageCard.Highlight(highlights[safeIndex]),
            )
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                IconButton(
                    onClick = { index = (safeIndex - 1).coerceAtLeast(0) },
                    enabled = safeIndex > 0,
                ) {
                    Icon(Icons.Filled.ChevronLeft, contentDescription = "Previous highlight")
                }
                Box(
                    modifier = Modifier
                        .weight(1f)
                        .clip(RoundedCornerShape(20.dp))
                        .background(
                            Brush.linearGradient(
                                colors = listOf(
                                    SpendTelReportColors.amber.copy(alpha = 0.18f),
                                    SpendTelReportColors.coral.copy(alpha = 0.12f),
                                ),
                            ),
                        )
                        .padding(horizontal = 28.dp, vertical = 32.dp),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(
                        text = highlights[safeIndex],
                        style = MaterialTheme.typography.headlineSmall,
                        fontWeight = FontWeight.Bold,
                        color = SpendTelReportColors.ink,
                        textAlign = TextAlign.Center,
                        lineHeight = 34.sp,
                    )
                }
                IconButton(
                    onClick = { index = (safeIndex + 1).coerceAtMost(highlights.lastIndex) },
                    enabled = safeIndex < highlights.lastIndex,
                ) {
                    Icon(Icons.Filled.ChevronRight, contentDescription = "Next highlight")
                }
            }
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                highlights.forEachIndexed { dotIndex, _ ->
                    val selected = dotIndex == safeIndex
                    Box(
                        modifier = Modifier
                            .padding(horizontal = 4.dp)
                            .size(if (selected) 10.dp else 7.dp)
                            .clip(CircleShape)
                            .background(
                                if (selected) SpendTelReportColors.amber else SpendTelReportColors.inkMuted.copy(alpha = 0.35f),
                            )
                            .clickable { index = dotIndex },
                    )
                }
            }
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun WebEquivalenceExplorer(
    equivalences: List<SpendingEquivalence>,
    currency: String,
) {
    var selectedIndex by remember(equivalences) { mutableIntStateOf(0) }
    val selected = equivalences[selectedIndex.coerceIn(0, equivalences.lastIndex)]

    StoryModulePanel {
        Column(verticalArrangement = Arrangement.spacedBy(18.dp)) {
            ReportSectionHeader(
                title = "Spending equivalences",
                subtitle = "Pick a comparison to explore",
                accent = SpendTelReportColors.coral,
                icon = Icons.Outlined.ShoppingBag,
            )
            FlowRow(
                horizontalArrangement = Arrangement.spacedBy(10.dp),
                verticalArrangement = Arrangement.spacedBy(10.dp),
            ) {
                equivalences.forEachIndexed { index, equivalence ->
                    val active = index == selectedIndex
                    val borderColor by animateColorAsState(
                        targetValue = if (active) SpendTelReportColors.coral else Color(0xFFE2E8F0),
                        animationSpec = spring(stiffness = Spring.StiffnessMediumLow),
                        label = "equivalenceChipBorder",
                    )
                    Surface(
                        modifier = Modifier.clickable { selectedIndex = index },
                        shape = RoundedCornerShape(999.dp),
                        color = if (active) SpendTelReportColors.coral.copy(alpha = 0.12f) else Color.White,
                        border = androidx.compose.foundation.BorderStroke(1.5.dp, borderColor),
                    ) {
                        Text(
                            text = equivalence.sourceLabel,
                            modifier = Modifier.padding(horizontal = 16.dp, vertical = 10.dp),
                            style = MaterialTheme.typography.labelLarge,
                            fontWeight = if (active) FontWeight.Bold else FontWeight.Medium,
                            color = SpendTelReportColors.ink,
                        )
                    }
                }
            }
            GradientHeroCard(gradient = SpendTelGradients.storyWarm) {
                Text(
                    text = selected.sourceLabel.uppercase(),
                    style = MaterialTheme.typography.labelMedium,
                    color = Color.White.copy(alpha = 0.75f),
                )
                Text(
                    text = formatCurrency(selected.amount, currency),
                    style = MaterialTheme.typography.displaySmall,
                    fontWeight = FontWeight.Bold,
                    color = Color.White,
                )
                Text(
                    text = selected.comparison,
                    style = MaterialTheme.typography.headlineMedium,
                    fontWeight = FontWeight.Bold,
                    color = Color.White,
                )
                Text(
                    text = selected.insight,
                    style = MaterialTheme.typography.bodyLarge,
                    color = Color.White.copy(alpha = 0.92f),
                    lineHeight = 24.sp,
                )
            }
            ShareCardActions(
                content = selected.shareContent(currency),
                imageCard = ShareImageCard.Equivalence(selected, currency),
            )
        }
    }
}

@Composable
private fun WebQuickStatsStrip(report: ExpenseReport) {
    val stats = buildList {
        report.trends?.spendingVsLastReport?.let { add("Spending change" to it) }
        report.spendingPatterns?.diningShare?.let { add("Dining share" to "${it.toInt()}%") }
        report.spendingPatterns?.convenienceShare?.let { add("Convenience" to "${it.toInt()}%") }
        report.moneyLeaks?.totalMonthlySubscriptions?.let {
            add("Subscriptions/mo" to formatCurrency(it, report.dashboardCurrency()))
        }
        report.moneyLeaks?.totalFees?.let {
            add("Fees" to formatCurrency(it, report.dashboardCurrency()))
        }
    }
    if (stats.isEmpty()) return

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .horizontalScroll(rememberScrollState()),
        horizontalArrangement = Arrangement.spacedBy(14.dp),
    ) {
        stats.forEachIndexed { index, (label, value) ->
            val accent = accentForIndex(index)
            Column(
                modifier = Modifier
                    .width(180.dp)
                    .clip(RoundedCornerShape(18.dp))
                    .background(Color.White)
                    .border(1.dp, Color(0xFFE2E8F0), RoundedCornerShape(18.dp))
                    .padding(horizontal = 18.dp, vertical = 16.dp),
                verticalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                Text(
                    text = label.uppercase(),
                    style = MaterialTheme.typography.labelSmall,
                    color = SpendTelReportColors.inkMuted,
                    letterSpacing = 0.8.sp,
                )
                Text(
                    text = value,
                    style = MaterialTheme.typography.headlineSmall,
                    fontWeight = FontWeight.Bold,
                    color = accent.darkenForText(),
                )
            }
        }
    }
}

@Composable
private fun WebTrendsPanel(trends: ReportTrends) {
    StoryModulePanel {
        ReportSectionHeader(
            title = "Since your last story",
            subtitle = "Report-over-report shifts",
            accent = SpendTelReportColors.indigo,
            icon = Icons.Outlined.Timeline,
        )
        Spacer(modifier = Modifier.height(12.dp))
        SoftInsightCard(accent = SpendTelReportColors.indigo) {
            trends.spendingVsLastReport?.let { WebDetailBlock("Spending change", it) }
            trends.personalityShift?.let { WebDetailBlock("Personality shift", it) }
            trends.recurringTheme?.let { WebDetailBlock("Recurring theme", it) }
        }
    }
}

@Composable
private fun WebFirstReportComparisonPanel() {
    StoryModulePanel {
        SoftInsightCard(accent = SpendTelReportColors.indigo) {
            Text(
                text = "Your next report will compare against this one — shifts in spending, personality, and habits will show up here.",
                style = MaterialTheme.typography.bodyLarge,
                color = SpendTelReportColors.ink,
                lineHeight = 24.sp,
            )
        }
    }
}

@Composable
private fun WebWhatChangedCompact(changes: WhatChanged) {
    StoryModulePanel {
        WhatChangedSection(changes = changes)
    }
}

@Composable
private fun WebRhythmsPanel(
    patterns: SpendingPatterns,
    currency: String,
) {
    StoryModulePanel {
        ReportSectionHeader(
            title = "Your rhythms",
            subtitle = "When and where spending shows up",
            accent = SpendTelReportColors.teal,
            icon = Icons.Outlined.Timeline,
        )
        Spacer(modifier = Modifier.height(12.dp))
        patterns.weekdayVsWeekend?.let { WebDetailBlock("Weekday vs weekend", it) }
        patterns.busiestSpendingDay?.let { WebDetailBlock("Busiest day", it) }
        if (patterns.topMerchantsByShare.isNotEmpty()) {
            Spacer(modifier = Modifier.height(8.dp))
            Text(
                text = "Top merchants",
                style = MaterialTheme.typography.titleSmall,
                fontWeight = FontWeight.Bold,
                color = SpendTelReportColors.ink,
            )
            patterns.topMerchantsByShare.forEach { merchant ->
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(vertical = 6.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                ) {
                    Text(text = merchant.merchant, color = SpendTelReportColors.ink)
                    Text(
                        text = "${merchant.percent.toInt()}% · ${formatCurrency(merchant.total, currency)}",
                        fontWeight = FontWeight.Bold,
                        color = SpendTelReportColors.teal.darkenForText(),
                    )
                }
            }
        }
    }
}

@Composable
private fun WebMoneyLeaksPanel(moneyLeaks: MoneyLeaks, currency: String) {
    StoryModulePanel {
        Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
            ReportSectionHeader(
                title = "Money leaks",
                subtitle = "Recurring charges we spotted",
                accent = SpendTelReportColors.increase,
                icon = Icons.Outlined.Repeat,
            )
            moneyLeaks.summary?.let {
                Text(
                    text = it,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = SpendTelReportColors.increase,
                )
            }
            Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                moneyLeaks.totalMonthlySubscriptions?.let { total ->
                    WebMoneyLeakStat(
                        label = "Monthly subs",
                        value = formatCurrency(total, currency),
                        modifier = Modifier.weight(1f),
                    )
                }
                moneyLeaks.totalFees?.let { total ->
                    WebMoneyLeakStat(
                        label = "Fees",
                        value = formatCurrency(total, currency),
                        modifier = Modifier.weight(1f),
                    )
                }
            }
            moneyLeaks.subscriptions.take(3).forEach { subscription ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                ) {
                    Column {
                        Text(subscription.name, fontWeight = FontWeight.SemiBold)
                        Text(
                            text = subscription.frequency,
                            style = MaterialTheme.typography.labelSmall,
                            color = SpendTelReportColors.inkMuted,
                        )
                    }
                    Text(
                        text = formatCurrency(subscription.amount, currency),
                        fontWeight = FontWeight.Bold,
                        color = SpendTelReportColors.increase,
                    )
                }
            }
        }
    }
}

@Composable
private fun WebMoneyLeakStat(
    label: String,
    value: String,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier
            .clip(RoundedCornerShape(16.dp))
            .background(SpendTelReportColors.increase.copy(alpha = 0.08f))
            .padding(14.dp),
    ) {
        Text(label, style = MaterialTheme.typography.labelMedium, color = SpendTelReportColors.inkMuted)
        Text(value, style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold, color = SpendTelReportColors.increase)
    }
}

@Composable
private fun WebMerchantGrid(
    merchants: List<MerchantInsight>,
    currency: String,
) {
    var expandedMerchant by remember { mutableStateOf<String?>(null) }

    StoryModulePanel {
        Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
            ReportSectionHeader(
                title = "Merchant intelligence",
                subtitle = "Click a merchant to see the full pattern",
                accent = SpendTelReportColors.sky,
                icon = Icons.Outlined.ShoppingBag,
            )
            merchants.chunked(2).forEach { rowMerchants ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(16.dp),
                ) {
                    rowMerchants.forEach { merchant ->
                        val accent = accentForIndex(merchants.indexOf(merchant))
                        val expanded = expandedMerchant == merchant.merchant
                        Column(
                            modifier = Modifier
                                .weight(1f)
                                .clip(RoundedCornerShape(18.dp))
                                .background(if (expanded) accent.copy(alpha = 0.1f) else Color(0xFFF8FAFC))
                                .border(
                                    width = if (expanded) 2.dp else 1.dp,
                                    color = if (expanded) accent else Color(0xFFE2E8F0),
                                    shape = RoundedCornerShape(18.dp),
                                )
                                .clickable {
                                    expandedMerchant = if (expanded) null else merchant.merchant
                                }
                                .padding(16.dp),
                            verticalArrangement = Arrangement.spacedBy(8.dp),
                        ) {
                            Text(
                                text = merchant.merchant,
                                fontWeight = FontWeight.Bold,
                                color = SpendTelReportColors.ink,
                            )
                            Text(
                                text = formatCurrency(merchant.total, currency),
                                style = MaterialTheme.typography.titleMedium,
                                fontWeight = FontWeight.SemiBold,
                                color = accent.darkenForText(),
                            )
                            Text(
                                text = "${merchant.visits} visits",
                                style = MaterialTheme.typography.labelMedium,
                                color = SpendTelReportColors.inkMuted,
                            )
                            merchant.trend?.let {
                                Text(
                                    text = it.replaceFirstChar { char -> char.titlecase() },
                                    style = MaterialTheme.typography.labelLarge,
                                    color = accent.darkenForText(),
                                )
                            }
                            if (expanded) {
                                Text(
                                    text = merchant.pattern,
                                    style = MaterialTheme.typography.bodyMedium,
                                    color = SpendTelReportColors.inkMuted,
                                    lineHeight = 22.sp,
                                )
                            }
                        }
                    }
                    if (rowMerchants.size == 1) {
                        Spacer(modifier = Modifier.weight(1f))
                    }
                }
            }
        }
    }
}

@Composable
private fun WebCollapsibleStoryModule(
    title: String,
    subtitle: String,
    initiallyExpanded: Boolean = false,
    content: @Composable () -> Unit,
) {
    var expanded by remember { mutableStateOf(initiallyExpanded) }

    StoryModulePanel {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clickable { expanded = !expanded },
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Column(modifier = Modifier.weight(1f)) {
                    Text(title, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                    Text(subtitle, style = MaterialTheme.typography.bodySmall, color = SpendTelReportColors.inkMuted)
                }
                Icon(
                    imageVector = if (expanded) Icons.Filled.ExpandLess else Icons.Filled.ExpandMore,
                    contentDescription = null,
                )
            }
            if (expanded) {
                content()
            }
        }
    }
}

@Composable
private fun WebDetailBlock(label: String, value: String) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 4.dp),
        verticalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        Text(
            text = label,
            style = MaterialTheme.typography.labelLarge,
            fontWeight = FontWeight.SemiBold,
            color = SpendTelReportColors.inkMuted,
        )
        Text(
            text = value,
            style = MaterialTheme.typography.bodyMedium,
            color = SpendTelReportColors.ink,
            lineHeight = 22.sp,
        )
    }
}

private fun Color.darkenForText(): Color {
    return Color(red = red * 0.75f, green = green * 0.75f, blue = blue * 0.75f, alpha = alpha)
}
